// Ubicación en el repo: src/lib/publicScheduling.js
// Cliente del auto-agendamiento público (spec-final-autoagendamiento.md) —
// wrappers sobre las 4 Edge Functions públicas (`public-agendamiento-*`,
// DECISIONS.md #71) más la validación de RUT que también corre del lado
// del servidor (acá solo es UX: falla rápido antes de llamar a la red;
// la validación real y obligatoria vive en public-agendamiento-create).
//
// Usa el mismo cliente `supabase` (anon key) que el resto de la app — las
// 4 funciones son públicas y no requieren sesión, así que no hace falta
// nada especial acá, solo `supabase.functions.invoke(...)` como ya se usa
// en src/lib/appointments.js y src/lib/professionals.js.
import { supabase } from './supabaseClient';

async function invoke(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    // Mismo patrón que professionals.js: supabase-js no siempre expone el
    // body del error en error.message, hay que leerlo del context.
    const detail = await error.context?.json?.().catch(() => null);
    const err = new Error(detail?.error || error.message || 'Ocurrió un error inesperado.');
    err.conflict = detail?.conflict === true;
    throw err;
  }
  return data;
}

export function fetchPublicConfig() {
  return invoke('public-agendamiento-config', {});
}

export function lookupPatientByRut(rut) {
  return invoke('public-agendamiento-lookup-rut', { rut });
}

export function fetchAvailability({ branchId, type, professionalId }) {
  return invoke('public-agendamiento-availability', { branchId, type, professionalId: professionalId || undefined });
}

export function createBooking(payload) {
  return invoke('public-agendamiento-create', payload);
}

// Página de cancelación pública por token (spec sección 7, DECISIONS.md
// #74) — misma Edge Function, dos acciones: 'lookup' solo lee (se usa al
// cargar la página), 'cancel' recién ahí escribe y exige que el paciente
// haya hecho clic explícito en "Sí, cancelar mi hora".
export function lookupCancellation(token) {
  return invoke('public-agendamiento-cancel', { token, action: 'lookup' });
}

export function confirmCancellation(token) {
  return invoke('public-agendamiento-cancel', { token, action: 'cancel' });
}

// ---- RUT: mismo algoritmo que sanitize_rut() (Postgres) + el módulo 11
// que ya se usó en el prototipo y en public-agendamiento-create (Deno) —
// tres implementaciones del mismo algoritmo, a propósito, para poder
// validar en cada capa sin depender de una petición de red.
export function sanitizeRutClient(v) {
  return (v || '').replace(/[^0-9kK-]/g, '').toUpperCase();
}

export function isValidRut(rutCompleto) {
  const limpio = (rutCompleto || '').replace(/-/g, '').replace(/[^0-9kK]/g, '').toUpperCase();
  if (limpio.length < 2) return false;
  const cuerpo = limpio.slice(0, -1);
  const dv = limpio.slice(-1);
  if (!/^\d+$/.test(cuerpo)) return false;
  let suma = 0;
  let mult = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i], 10) * mult;
    mult = mult === 7 ? 2 : mult + 1;
  }
  const resto = 11 - (suma % 11);
  const dvCalculado = resto === 11 ? '0' : resto === 10 ? 'K' : String(resto);
  return dvCalculado === dv;
}

// Formatea "123456789" -> "12.345.678-9" solo para mostrar; lo que se
// manda al servidor siempre es la versión sin puntos (mismo formato que
// sanitize_rut).
export function formatRutDisplay(rut) {
  const clean = sanitizeRutClient(rut);
  if (!clean.includes('-')) return clean;
  const [body, dv] = clean.split('-');
  const withDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withDots}-${dv}`;
}

export function isValidBirthdateClient(iso) {
  if (!iso) return false;
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d > today) return false;
  const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
  if (d < minDate) return false;
  return true;
}

// formatDateLong/formatDate ya existen en ../lib/format.js (usados en toda
// la app — comprobante de cita, Agenda) — antes se duplicaba acá la misma
// lógica sin motivo real. Se elimina el duplicado (20-ago-2026,
// DECISIONS.md #74): PublicScheduling.jsx ahora importa directo de
// ../lib/format, así el formato de fecha nunca puede desincronizarse entre
// la página pública y el resto del software.